import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../../firebase/config';
import { 
    collection, 
    getDocs, 
    query, 
    where
} from "firebase/firestore";

// Importaciones del Contexto y Utilidades Centralizadas (Pilares del Canvas)
import { useSegurosData } from '../../context/SegurosDataContext';
import { saveToFirestore, sortAlphabetically } from '../../firebase/cleanPayload';
import { cleanNumeric } from '../../utils/segurosUtils';

const CANALES_DISPONIBLES = ['Agente', 'Pas', 'Operatoria', 'Directo'];

const initialFormState = {
    persona: '',
    nombre: '',
    apellido: '',
    dni: '',
    cuit: '',
    email: '',
    telefono: '',
    canal: '',             
    canalNombreId: '',      
    canalNombre: '',        
    subcanalNombreId: '',   
    subcanalNombre: '',     
    tieneContador: false,
    contadorId: '',
    contadorNombre: ''
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * ModalProspecto - Gestión, creación y edición de prospectos comerciales.
 * Optimizado mediante consumo de caché global, ordenamiento unificado y persistencia con auditoría.
 */
const ModalProspecto = ({ show, onClose, onSaveSuccess, initialData = null }) => {
    // --- 1. CONSUMO DEL CONTEXTO MAESTRO GLOBAL ---
    const { dbMasters } = useSegurosData();

    // --- 2. ESTADOS LOCALES ---
    const [isSaving, setIsSaving] = useState(false);
    const [localError, setLocalError] = useState(null);
    const [dbSubcanales, setDbSubcanales] = useState([]); 
    const [formData, setFormData] = useState(initialFormState);

    // --- 3. PROCESO DE HIDRATACIÓN DINÁMICA ---
    useEffect(() => {
        if (show) {
            if (initialData) {
                const mappedData = {};
                Object.keys(initialFormState).forEach(key => {
                    const val = initialData[key];
                    mappedData[key] = (val === undefined || val === null || val === 'undefined') ? '' : val;
                });
                setFormData({ ...initialFormState, ...mappedData });
            } else {
                setFormData(initialFormState);
            }
            setLocalError(null);
        }
    }, [show, initialData]);

    // --- 4. CARGA ASÍNCRONA DE SUBCANALES DEPENDIENTES ---
    useEffect(() => {
        const fetchSubcanales = async () => {
            if (!formData.canalNombreId) { 
                setDbSubcanales([]); 
                return; 
            }
            try {
                const q = query(collection(db, 'public/data/subcanales'), where('canalId', '==', formData.canalNombreId));
                const snap = await getDocs(q);
                setDbSubcanales(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { 
                console.error("Error al recuperar subcanales del prospecto:", e); 
            }
        };
        fetchSubcanales();
    }, [formData.canalNombreId]);

    // --- 5. MEMORIZACIÓN Y ORDENAMIENTO EN CACHÉ LOCAL (useMemo & sortAlphabetically) ---
    const sortedCanalesDisponibles = useMemo(() => {
        return sortAlphabetically(CANALES_DISPONIBLES);
    }, []);

    const sortedCanales = useMemo(() => {
        const list = dbMasters?.canales || [];
        // Filtramos de forma flexible e insensible a mayúsculas
        const filtrados = list.filter(c => {
            const canalType = String(c.tipo || c.canal || '').toUpperCase().trim();
            const formCanal = String(formData.canal || '').toUpperCase().trim();
            return canalType === formCanal;
        });
        return sortAlphabetically(filtrados, 'nombreDisplay');
    }, [dbMasters?.canales, formData.canal]);

    const sortedSubcanales = useMemo(() => {
        return sortAlphabetically(dbSubcanales, 'nombre');
    }, [dbSubcanales]);

    const sortedContadores = useMemo(() => {
        return sortAlphabetically(dbMasters?.contadores || [], 'nombre');
    }, [dbMasters?.contadores]);

    // --- 6. HANDLERS DE ACCIÓN ---
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        if (type === 'text' && !['email', 'telefono'].includes(name)) {
            finalValue = value.toUpperCase();
        }
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : finalValue }));
        if (localError) setLocalError(null);
    };

    const handleTipoCanalChange = (e) => {
        setFormData(prev => ({ 
            ...prev, 
            canal: e.target.value, 
            canalNombreId: '', 
            canalNombre: '', 
            subcanalNombreId: '', 
            subcanalNombre: '' 
        }));
    };

    const handleNombreCanalChange = (e) => {
        const selectedId = e.target.value;
        const obj = sortedCanales.find(c => c.id === selectedId);
        setFormData(prev => ({ 
            ...prev, 
            canalNombreId: selectedId, 
            canalNombre: obj ? obj.nombreDisplay : '', 
            subcanalNombreId: '', 
            subcanalNombre: '' 
        }));
    };

    const handleVendedorAsociadoChange = (e) => {
        const selectedId = e.target.value;
        const obj = sortedSubcanales.find(s => s.id === selectedId);
        setFormData(prev => ({ 
            ...prev, 
            subcanalNombreId: selectedId, 
            subcanalNombre: obj ? obj.nombre : '' 
        }));
    };

    const handleContadorChange = (e) => {
        const selectedId = e.target.value;
        const obj = sortedContadores.find(c => c.id === selectedId);
        setFormData(prev => ({ 
            ...prev, 
            contadorId: selectedId, 
            contadorNombre: obj ? `${obj.nombre} ${obj.apellido || ''}`.trim() : '' 
        }));
    };

    // --- 7. PROCESAMIENTO Y GUARDADO FINAL CENTRALIZADO ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSaving) return;
        setLocalError(null);

        if (formData.email && !isValidEmail(formData.email)) {
            setLocalError('Correo electrónico inválido.');
            return;
        }

        const dniLimpio = cleanNumeric(formData.dni);
        const cuitLimpio = cleanNumeric(formData.cuit);

        setIsSaving(true);
        try {
            const collectionPath = 'public/data/prospectos';
            
            // PAYLOAD BASE: Excluimos campos de control de React/Firebase
            const { id: _, path: __, _entidadFull: ___, nombreDisplay: ____, ...dataToSave } = formData;
            
            const rawPayload = {
                ...dataToSave,
                dni: dniLimpio,
                cuit: cuitLimpio,
                tipoEntidad: 'PROSPECTO'
            };

            // saveToFirestore se encarga de aplicar cleanPayload internamente, agregar serverTimestamp
            // para creación y modificación, y estampar la auditoría del operador de forma centralizada.
            const savedProspecto = await saveToFirestore(collectionPath, rawPayload, initialData?.id || null);

            onSaveSuccess(initialData?.id ? 'Prospecto actualizado correctamente' : 'Prospecto creado correctamente', savedProspecto);
            onClose();
        } catch (error) {
            console.error("Error al guardar prospecto comercial:", error);
            setLocalError('Error de servidor: ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-slate-900/60 z-[4000] flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden animate-fade-in text-left">
                <div className="flex justify-between items-center px-8 py-5 border-b border-slate-100 shrink-0 bg-white">
                    <div className="flex items-center gap-3">
                        <span className={`p-2 rounded-lg ${initialData ? 'bg-blue-100 text-blue-600' : 'bg-orange-100 text-orange-600'}`}>
                             <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                        </span>
                        <div>
                            <h3 className="text-2xl font-bold text-slate-800 leading-none">{initialData ? 'Editar Prospecto' : 'Nuevo Prospecto'}</h3>
                            <p className="text-[10px] font-bold mt-1 tracking-widest uppercase opacity-50">{initialData ? 'Actualización de Datos' : 'Alta de Prospecto'}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-all">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                <div className="overflow-y-auto p-8 flex-grow bg-white custom-scrollbar">
                    {localError && <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-sm text-red-700 font-bold rounded-r-lg">{localError}</div>}
                    
                    <form onSubmit={handleSubmit} className="space-y-8">
                        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-slate-50/10">
                            <legend className="px-2 text-lg font-bold text-slate-700 bg-white absolute -top-3 left-4">Datos del Prospecto</legend>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pt-2">
                                <div>
                                    <label className="block text-sm font-bold text-slate-600 mb-1">Persona *</label>
                                    <select name="persona" value={formData.persona} onChange={handleChange} className="input-round" required>
                                        <option value="">Seleccione...</option>
                                        <option value="FISICA">FISICA</option>
                                        <option value="JURIDICA">JURIDICA</option>
                                    </select>
                                </div>
                                {formData.persona === 'FISICA' ? (
                                    <>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">Nombre *</label><input type="text" name="nombre" value={formData.nombre} onChange={handleChange} className="input-round" required /></div>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">Apellido *</label><input type="text" name="apellido" value={formData.apellido} onChange={handleChange} className="input-round" required /></div>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">DNI *</label><input type="text" name="dni" value={formData.dni} onChange={handleChange} className="input-round" required /></div>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">CUIT</label><input type="text" name="cuit" value={formData.cuit} onChange={handleChange} className="input-round" /></div>
                                    </>
                                ) : formData.persona === 'JURIDICA' ? (
                                    <>
                                        <div className="md:col-span-2"><label className="block text-sm font-bold text-slate-600 mb-1">Razón Social *</label><input type="text" name="nombre" value={formData.nombre} onChange={handleChange} className="input-round" required /></div>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">CUIT *</label><input type="text" name="cuit" value={formData.cuit} onChange={handleChange} className="input-round" required /></div>
                                    </>
                                ) : null}
                                {formData.persona && (
                                    <>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">Email *</label><input type="email" name="email" value={formData.email} onChange={handleChange} className="input-round lowercase" required /></div>
                                        <div><label className="block text-sm font-bold text-slate-600 mb-1">Teléfono *</label><input type="text" name="telefono" value={formData.telefono} onChange={handleChange} className="input-round" placeholder="Ej. 5491132797718" required /></div>
                                    </>
                                )}
                            </div>
                        </fieldset>

                        <fieldset className="border border-orange-200 rounded-xl p-6 relative mt-4 bg-orange-50/10">
                            <legend className="px-2 text-lg font-bold text-orange-700 bg-white absolute -top-3 left-4">Estructura Comercial</legend>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-2">
                                <div>
                                    <label className="block text-sm font-bold text-slate-600 mb-1">Tipo Canal</label>
                                    <select name="canal" value={formData.canal} onChange={handleTipoCanalChange} className="input-round font-bold">
                                        <option value="">Seleccione...</option>
                                        {sortedCanalesDisponibles.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-600 mb-1">Nombre Canal</label>
                                    <select name="canalNombreId" value={formData.canalNombreId} onChange={handleNombreCanalChange} className="input-round font-semibold" disabled={!formData.canal}>
                                        <option value="">Seleccione...</option>
                                        {sortedCanales.map(c => <option key={c.id} value={c.id}>{c.nombreDisplay}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-600 mb-1">Subcanal Asociado</label>
                                    <select name="subcanalNombreId" value={formData.subcanalNombreId} onChange={handleVendedorAsociadoChange} className="input-round" disabled={!formData.canalNombreId}>
                                        <option value="">(Ninguno)</option>
                                        {sortedSubcanales.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                                    </select>
                                </div>
                                
                                <div className="md:col-span-3 bg-white/50 p-4 rounded-xl border border-orange-100 mt-2">
                                    <div className="flex items-center mb-3">
                                        <input type="checkbox" name="tieneContador" id="tieneContador" checked={formData.tieneContador} onChange={handleChange} className="w-4 h-4 text-orange-600 rounded cursor-pointer transition-all" />
                                        <label htmlFor="tieneContador" className="ml-2 text-sm text-slate-700 font-bold cursor-pointer">¿Tiene Contador Asociado?</label>
                                    </div>
                                    {formData.tieneContador && (
                                        <select name="contadorId" value={formData.contadorId} onChange={handleContadorChange} className="input-round font-bold">
                                            <option value="">Seleccione Contador...</option>
                                            {sortedContadores.map(c => <option key={c.id} value={c.id}>{c.nombreCompleto || `${c.nombre} ${c.apellido || ''}`}</option>)}
                                        </select>
                                    )}
                                </div>
                            </div>
                        </fieldset>
                    </form>
                </div>

                <div className="px-8 py-5 border-t border-slate-100 flex justify-end gap-3 shrink-0 bg-white select-none">
                    <button onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 text-xs uppercase tracking-widest transition-all active:scale-95">Cancelar</button>
                    <button onClick={handleSubmit} disabled={isSaving} className={`px-8 py-2.5 rounded-full text-white font-black transition shadow-lg disabled:opacity-50 min-w-[160px] text-xs uppercase tracking-widest active:scale-95 ${initialData ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-200' : 'bg-orange-600 hover:bg-orange-700 shadow-orange-200'}`}>
                        {isSaving ? 'Guardando...' : (initialData ? 'Actualizar Datos' : 'Crear Prospecto')}
                    </button>
                </div>
            </div>
            
            <style>{`
                .input-round { width: 100%; border: 1px solid #e2e8f0; border-radius: 0.75rem; padding: 0.65rem 1rem; font-size: 0.95rem; outline: none; transition: all 0.2s; background-color: #fff; text-align: left; }
                .input-round:focus { border-color: #f97316; box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1); }
                .input-round:disabled { background-color: #f8fafc; cursor: not-allowed; opacity: 0.7; }
                .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>,
        document.body
    );
};

export default ModalProspecto;